import { h } from 'preact';
import {
  useCallback,
  useContext,
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'preact/hooks';
import classNames from 'classnames';

import {
  getMessageAgentInformation,
  getLastMessagePreview,
  setKustomerLocalStorage,
  getLastAgentMessageInConversation,
} from 'widget_main/globals/helpers';
import {
  getSessionListAvatarUserDisplayName,
  getSessionListLine1Title,
  resolveSessionListRowAvatarUrl,
} from 'widget_main/components/Avatar/avatarHelpers';
import {
  NavigationContext,
  ConversationsContext,
  SettingsContext,
  UnreadCountsContext,
  ChatAvailabilityContext,
  HideNewConversationButtonSettingsContext,
  MessagesContext,
} from 'widget_main/store';
import {
  CONVERSATIONS_PATH,
  MAX_SESSION_HISTORY_PREFETCH_ITERATIONS,
  MESSAGE_THREAD_PATH,
  WIDGET_TYPE_CHAT_KB,
} from 'widget_main/globals/constants';
import * as keyboardEventHelpers from 'widget_main/globals/keyboardEvents';

import ConversationsEmpty from 'widget_main/globals/assets/conversationsEmpty.svg';
import Offline from 'widget_main/globals/assets/offline.svg';
import Avatar from 'widget_main/components/Avatar';
import TimeAgo from 'widget_main/components/TimeAgo';
import Unread from 'widget_main/components/Unread';
import NewConversationButton from 'widget_main/components/NewConversationButton';
import Markdown from 'widget_main/components/Markdown/Markdown';

import { ConversationEntity } from 'widget_main/store/types';

import { useTranslations } from 'i18n/hooks';
import { sanitizeHtml } from 'widget_main/vendors/hooks';
import {
  getFilteredConversations,
  sortConversationsForHistory,
} from 'widget_main/screens/Conversations/helpers';
import { getStore } from 'globals/coreStore';
import * as chatHistoryStatusFilter from 'globals/chatHistoryStatusFilter';
import DecorativeImage from 'widget_main/components/Image/DecorativeImage';
import messages from './messages';

import styles from './conversations.scss';
import Loader from '../../components/ui/Loader';

const Conversations = () => {
  const chatAvailability = useContext(ChatAvailabilityContext);
  const hideNewConversationButtonSettings = useContext(
    HideNewConversationButtonSettingsContext,
  );
  const navigation = useContext(NavigationContext);
  const conversations = useContext(ConversationsContext);
  const messagesState = useContext(MessagesContext);
  const unreadCounts = useContext(UnreadCountsContext);
  const chatSettings = useContext(SettingsContext);

  const translations = useTranslations(messages);

  const historyFilterSignature = (() => {
    const s = getStore();
    return `${s.enableHistoryStatusFilter}:${
      s.historyStatusFilter
    }:${JSON.stringify(s.customHistoryFilter ?? [])}`;
  })();

  const isConversationCollapsed = useCallback(
    (conversation: ConversationEntity) => {
      const store = getStore();
      if (!store.enableHistoryStatusFilter) {
        return false;
      }
      const unreadCount =
        unreadCounts.conversations[conversation.conversationId] ?? 0;
      return chatHistoryStatusFilter.isChatHistoryRowFiltered(
        {
          conversationId: conversation.conversationId,
          ended: conversation.ended,
          custom: chatHistoryStatusFilter.normalizeSessionCustomAttributes(
            conversation.custom,
          ),
        },
        unreadCount,
        {
          historyStatusFilter: (store.historyStatusFilter ??
            'all') as chatHistoryStatusFilter.HistoryStatusFilterValue,
          customHistoryFilter: store.customHistoryFilter,
        },
      );
    },
    [unreadCounts.conversations],
  );

  const [, forceUpdate] = useState({});

  useEffect(() => {
    setKustomerLocalStorage('lastOpenPage', CONVERSATIONS_PATH);

    const interval = setInterval(() => {
      forceUpdate({});
    }, 60000);
    return () => clearInterval(interval);
  }, []);
  const [fetchingHistory, setFetchingHistory] = useState(false);
  const previousScrollTop = useRef(0);
  const historyDrainAttempts = useRef(0);
  const previousHistoryFilterSignature = useRef(historyFilterSignature);
  const { fetchNextPageOfHistory } = conversations;
  const CONVERSATION_LIST_ID = 'conversationList';
  const conversationCount = Object.keys(conversations.data).length;
  useLayoutEffect(() => {
    // this hook will run only on first load to scroll to the last scroll location
    if (conversationCount && navigation.conversationListScrollLocation) {
      const conversationList = document.getElementById(
        CONVERSATION_LIST_ID,
      ) as HTMLElement;
      conversationList.scrollTop = navigation.conversationListScrollLocation;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useLayoutEffect(() => {
    // this hook will make sure that the scroll position is retained when loading older conversations
    if (conversationCount) {
      const conversationList = document.getElementById(
        CONVERSATION_LIST_ID,
      ) as HTMLElement;
      if (previousScrollTop.current) {
        conversationList.scrollTop = previousScrollTop.current;
        previousScrollTop.current = 0;
      }
    }
  }, [conversationCount]);
  useEffect(() => {
    // this hook clears the loading ui when messages are received
    if (conversationCount || !conversations.pages?.next)
      setFetchingHistory(false);
  }, [conversationCount, conversations.pages?.next]);

  useEffect(() => {
    if (previousHistoryFilterSignature.current !== historyFilterSignature) {
      previousHistoryFilterSignature.current = historyFilterSignature;
      historyDrainAttempts.current = 0;
    }

    const base = getFilteredConversations(
      Object.values(conversations.data),
      chatSettings.noHistory,
      Object.keys(unreadCounts.conversations),
    );
    const visibleCount = base.reduce((acc, c) => {
      return acc + (isConversationCollapsed(c) ? 0 : 1);
    }, 0);

    if (visibleCount > 0) {
      historyDrainAttempts.current = 0;
      return;
    }

    if (!conversations.pages?.next || fetchingHistory) {
      return;
    }

    if (
      historyDrainAttempts.current >= MAX_SESSION_HISTORY_PREFETCH_ITERATIONS
    ) {
      return;
    }

    historyDrainAttempts.current += 1;
    setFetchingHistory(true);
    fetchNextPageOfHistory().finally(() => {
      setFetchingHistory(false);
    });
  }, [
    conversationCount,
    conversations.pages?.next,
    fetchingHistory,
    chatSettings.noHistory,
    unreadCounts.conversations,
    conversations.data,
    fetchNextPageOfHistory,
    historyFilterSignature,
    isConversationCollapsed,
  ]);
  const handleClickConversation = (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    navigation.updateCurrentConversationId(id);
    navigation.updatePage(MESSAGE_THREAD_PATH);
  };

  const handleKeyPress = (e, id) => {
    if (keyboardEventHelpers.isEnterKey(e)) {
      handleClickConversation(e, id);
    }
  };

  const getSortedConversations = () => {
    const filteredConversations = getFilteredConversations(
      Object.values(conversations.data),
      chatSettings.noHistory,
      Object.keys(unreadCounts.conversations),
    );

    return sortConversationsForHistory(
      filteredConversations,
      unreadCounts.conversations,
    );
  };

  const renderFilteredEmpty = () => {
    return (
      <div className={styles.empty} data-kt="filteredConversationList">
        <DecorativeImage
          src={ConversationsEmpty}
          className={styles.conversationsEmpty}
        />
        <h2 className={styles.emptyTitle}>{translations.filterEmptyTitle}</h2>
        <p className={styles.emptySubtitle}>
          {translations.filterEmptySubtitle}
        </p>
      </div>
    );
  };

  const renderEmpty = () => {
    if (chatAvailability === 'online') {
      return (
        <div className={styles.empty} data-kt="emptyConversationList">
          <DecorativeImage
            src={ConversationsEmpty}
            className={styles.conversationsEmpty}
          />
          <h2 className={styles.emptyTitle}>{translations.emptyTitle}</h2>
          <p className={styles.emptySubtitle}>{translations.emptySubtitle}</p>
        </div>
      );
    }

    return (
      <div className={styles.empty}>
        {chatSettings?.offhoursImageUrl ? (
          <DecorativeImage
            className={styles.offlineCustomImage}
            src={chatSettings?.offhoursImageUrl}
          />
        ) : (
          <DecorativeImage src={Offline} />
        )}
        <h2 className={styles.emptyTitle}>{translations.offHoursEmptyTitle}</h2>
        <p className={styles.emptySubtitle}>
          {translations.offHoursEmptySubtitle}
        </p>
      </div>
    );
  };

  const renderPreviewText = (previewText?) => {
    if (!previewText) {
      return null;
    }

    return (
      <Markdown
        markdown={previewText}
        formatHtml={(html) =>
          sanitizeHtml(
            html.replace(/<img.*>/g, translations.previewAttachmentReceived),
            {
              ALLOWED_TAGS: ['#text'],
            },
          )
        }
      />
    );
  };

  const renderConversationItem = (conversation: ConversationEntity) => {
    const { conversationId, lastMessageAt, ended } = conversation;

    const lastMessagePreview = getLastMessagePreview(conversation);

    const lastAgentMessage = getLastAgentMessageInConversation(
      conversation,
      messagesState,
    );

    const lastMessageAgentData = getMessageAgentInformation(
      lastAgentMessage?.sentBy ?? conversation.lastResponder,
      chatSettings,
    );

    const agentDisplayName = lastMessageAgentData?.displayName?.replace?.(
      /AI_/g,
      '',
    );
    const rawName =
      typeof conversation.name === 'string'
        ? sanitizeHtml(conversation.name, { ALLOWED_TAGS: [] })
        : '';
    const trimmedName = rawName.trim();
    const useLegacy = chatSettings.useLegacyConversationPreview === true;
    const line1Title = getSessionListLine1Title({
      useLegacy,
      previewDetailsName: conversation.previewDetailsName,
      conversationName: trimmedName.length > 0 ? trimmedName : undefined,
      agentDisplayName,
      teamName: chatSettings.teamName,
    });
    const resolvedRowAvatarUrl = resolveSessionListRowAvatarUrl({
      lastResponderAvatarUrl: lastAgentMessage?.sentBy?.avatarUrl,
      secondaryResponderAvatarUrl: conversation.lastResponder?.avatarUrl,
      teamIconUrl: chatSettings.teamIconUrl,
    });
    const avatarUserDisplayName = getSessionListAvatarUserDisplayName({
      resolvedAvatarUrl: resolvedRowAvatarUrl,
      agentDisplayName,
      teamName: chatSettings.teamName,
    });

    const isFilteredOut = isConversationCollapsed(conversation);

    return (
      <div
        role={isFilteredOut ? undefined : 'button'}
        tabIndex={isFilteredOut ? -1 : 0}
        key={conversationId}
        class={classNames(styles.conversation, {
          [styles.unreadConversation]:
            !!unreadCounts.conversations[conversationId],
          [styles.endedConversation]: ended,
          [styles.conversationFilteredOut]: isFilteredOut,
        })}
        aria-hidden={isFilteredOut ? true : undefined}
        onKeyPress={(e) => {
          if (isFilteredOut) return;
          handleKeyPress(e, conversationId);
        }}
        onClick={(e) => {
          if (isFilteredOut) return;
          handleClickConversation(e, conversationId);
        }}
      >
        <Avatar
          className={styles.avatar}
          size={40}
          avatarUrl={resolvedRowAvatarUrl}
          userDisplayName={avatarUserDisplayName}
        />
        <div
          class={styles.conversationDetails}
          data-kt="conversationRowDetails"
        >
          <div class={styles.conversationHeader}>
            <div class={styles.agentName}>{line1Title}</div>
            {ended && (
              <div className={styles.timestamp}>{translations.ended}</div>
            )}
            {!ended && lastMessageAt && (
              <TimeAgo className={styles.timestamp} timestamp={lastMessageAt} />
            )}
          </div>
          <div class={styles.conversationBody}>
            <div class={styles.preview}>
              {renderPreviewText(lastMessagePreview)}
            </div>
            <Unread
              className={styles.unreadProp}
              conversationId={conversationId}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderGetPreviousConversationsLoader = (isDrainingHistory = false) => {
    return (
      <div
        className={classNames(styles.loaderWrapper, {
          [styles.showLoader]:
            (fetchingHistory || isDrainingHistory) &&
            !!conversations.pages?.next,
        })}
      >
        <Loader className={styles.loader} />
      </div>
    );
  };
  const renderConversationsList = () => {
    const conversationsList = getSortedConversations();
    const visibleCount = conversationsList.reduce(
      (acc, c) => acc + (isConversationCollapsed(c) ? 0 : 1),
      0,
    );
    const isDrainingHistory =
      visibleCount === 0 &&
      !!conversations.pages?.next &&
      historyDrainAttempts.current < MAX_SESSION_HISTORY_PREFETCH_ITERATIONS;

    return (
      <div class={styles.conversationList}>
        {conversationsList.map(renderConversationItem)}
        {conversationsList.length === 0 && !isDrainingHistory && renderEmpty()}
        {conversationsList.length > 0 &&
          visibleCount === 0 &&
          !isDrainingHistory &&
          renderFilteredEmpty()}
        {renderGetPreviousConversationsLoader(isDrainingHistory)}
      </div>
    );
  };

  const renderNewConversationButton = () => {
    const openConversationFound = Object.values(conversations.data).find(
      (conversation) => !conversation.ended,
    );

    if (
      hideNewConversationButtonSettings.onChatHistory ||
      (chatSettings.singleSessionChat && openConversationFound)
    ) {
      return null;
    }

    return (
      <NewConversationButton
        data-kt="NewConversationButton"
        className={classNames(styles.createConversationButton, {
          [styles.createConversationButtonWithNavigation]:
            chatSettings.widgetType === WIDGET_TYPE_CHAT_KB,
        })}
      />
    );
  };
  const handleScrollConversationList = (e) => {
    const DISTANCE_TO_BOTTOM_TO_LOAD_MORE = 10;
    const element = e.target;
    navigation.updateConversationListScrollLocation(element.scrollTop);
    const isScrolledToBottom =
      element.scrollHeight -
        element.scrollTop -
        DISTANCE_TO_BOTTOM_TO_LOAD_MORE <=
      element.clientHeight;
    if (
      isScrolledToBottom &&
      conversationCount &&
      conversations.pages?.next &&
      !fetchingHistory
    ) {
      setFetchingHistory(true);
      previousScrollTop.current = element.scrollTop;
      conversations.fetchNextPageOfHistory();
    }
  };
  return (
    <div
      class={styles.container}
      onScroll={handleScrollConversationList}
      id={CONVERSATION_LIST_ID}
    >
      {renderConversationsList()}
      {renderNewConversationButton()}
    </div>
  );
};

export default Conversations;
